import React, { useState } from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { useTuslaReportedIncidentMutation } from '../../store/api/localApi';
import { StaffIncident } from '../../types';
import { useToast } from '../Toast';
import { useAuth } from '../../hooks/useAuth';

function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

interface Props {
  incident: StaffIncident;
}

/**
 * Statutory Tusla sub-record (S.I. 575/2018) — rendered ONLY for a
 * NOTIFIABLE incident (the caller conditionally mounts this; there is no
 * internal severity check here that could silently no-op for the wrong
 * incident). Shows the reporting deadline, whether it's been filed, and —
 * if not yet reported — an inline "mark reported" action with a required
 * reference, mirroring `incidentTuslaReportedSchema`'s server-side rule
 * that an unreferenced "reported" claim is not auditable.
 *
 * This is the surface an inspector looks at first: a NOTIFIABLE incident
 * past its deadline with no `tuslaReportedAt` is exactly what
 * `NotifiableOverdueBanner` is warning about on the register page — this
 * panel is where that gets resolved.
 *
 * The "mark as reported" action is `incident:manage` server-side —
 * admin-only (STAFF gets a clean 403). The control is gated on `isAdmin`
 * here too so staff never see an action they can't actually take; the
 * status display above it stays visible to everyone since it's read-only.
 */
const IncidentTuslaPanel: React.FC<Props> = ({ incident }) => {
  const { showError } = useToast();
  const { isAdmin } = useAuth();
  const [tuslaReported, { isLoading }] = useTuslaReportedIncidentMutation();
  const [reference, setReference] = useState('');
  const [showForm, setShowForm] = useState(false);

  const isReported = !!incident.tuslaReportedAt;
  const isOverdue =
    !isReported && !!incident.tuslaDeadlineAt && new Date(incident.tuslaDeadlineAt) < new Date();

  const handleMarkReported = async () => {
    const trimmed = reference.trim();
    if (!trimmed) return;
    try {
      await tuslaReported({ incidentId: incident.id, tuslaReference: trimmed }).unwrap();
      setShowForm(false);
      setReference('');
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data: { error?: string; message?: string } | undefined }).data
          : undefined;
      showError(data?.error || data?.message || 'Failed to record the Tusla report.');
    }
  };

  return (
    <div
      data-testid="incident-tusla-panel"
      className={`rounded-lg shadow p-5 border ${
        isOverdue ? 'bg-rose-50 border-rose-300' : 'bg-white border-gray-100'
      }`}
    >
      <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
        <ShieldExclamationIcon
          className={`h-4 w-4 ${isOverdue ? 'text-rose-600' : 'text-gray-400'}`}
          aria-hidden="true"
        />
        Tusla notification
      </h2>

      <dl className="mt-3 space-y-1.5 text-sm">
        {incident.tuslaDeadlineAt && (
          <div className="flex items-center gap-1.5">
            <dt className="font-medium text-gray-700">Deadline:</dt>
            <dd data-testid="incident-tusla-deadline" className={isOverdue ? 'text-rose-700' : ''}>
              {formatDateTime(incident.tuslaDeadlineAt)}
            </dd>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <dt className="font-medium text-gray-700">Status:</dt>
          {isReported ? (
            <dd data-testid="incident-tusla-status" className="text-emerald-700 font-medium">
              Reported {formatDateTime(incident.tuslaReportedAt!)}
              {incident.tuslaReference ? ` (ref: ${incident.tuslaReference})` : ''}
            </dd>
          ) : (
            <dd
              data-testid="incident-tusla-status"
              className={isOverdue ? 'text-rose-700 font-semibold' : 'text-amber-700'}
            >
              {isOverdue ? 'OVERDUE — not yet reported' : 'Not yet reported'}
            </dd>
          )}
        </div>
      </dl>

      {!isReported && isAdmin && (
        <div className="mt-3">
          {!showForm ? (
            <button
              type="button"
              data-testid="incident-tusla-mark-reported-btn"
              onClick={() => setShowForm(true)}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Mark as reported…
            </button>
          ) : (
            <div className="border-t border-gray-100 pt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tusla reference <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                data-testid="incident-tusla-reference-input"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. the reference given on the phone/portal report"
                className="block w-full rounded-md border-gray-300 text-sm"
              />
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  data-testid="incident-tusla-cancel-btn"
                  onClick={() => {
                    setShowForm(false);
                    setReference('');
                  }}
                  className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="incident-tusla-submit-btn"
                  onClick={handleMarkReported}
                  disabled={isLoading || !reference.trim()}
                  className="px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving…' : 'Confirm reported'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IncidentTuslaPanel;
